import type { CSSProperties, JSX } from 'react';
import { GAME_RULES } from '@ringmaster/shared';
import { GAME_MANIFEST } from '../assets/manifest.js';
import bloodmoonWordmark from '../assets/games/bloodmoon.svg';
import frameGold from '../assets/cosmetics/frame-gold.webp';
import frameSilver from '../assets/cosmetics/frame-silver.webp';
import bloodmoonTeaser from '../assets/video/bloodmoon-teaser.mp4';
import onenightTeaser from '../assets/video/onenight-teaser.mp4';
import { useEffect, useRef, useState } from 'react';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';
import { useReducedMotion } from '../fx/particles/useReducedMotion.js';
import { BackPill } from '../ui/BackPill.js';
import { VolumeControl } from '../ui/VolumeControl.js';
import { useTiltGlare, GLARE_CSS } from '../ui/tiltGlare.js';
import { useMenuMusic, playModePickLine } from '../audio/useMenuMusic.js';
import { stagePlayer } from '../audio/stagePlayer.js';
import { UI_SFX } from '../assets/audioManifest.js';

/**
 * GameSelectScreen — THE fullscreen game picker (one design everywhere).
 *
 * Three shapes, chosen by `mode` + `seated` + `showPicker`:
 *  - 'enter' + seated + showPicker (default true): the Discord Activity's boot
 *    gate for the FIRST seat (the one that just created the room/lobby) — a
 *    two-card "Select game mode" chooser (Classic / One-night). This is a
 *    deliberate two-choice front door: every other game (The Whisper, Omertà,
 *    future tiles) is absent here, not greyed — see AI_SEATS_SPEC-adjacent
 *    growth notes on the "can't get 5" problem. The tap is also the audio-unlock
 *    gesture upstream.
 *  - 'enter' + seated + !showPicker: a player joining a lobby someone else
 *    already created — the game is already chosen, so no picker; just a single
 *    "Enter" tap (still the audio-unlock gesture).
 *  - 'enter' + !seated: a game is underway — spectators get a single "watch"
 *    entry (unchanged).
 *  - 'switch': the in-lobby / end-screen "Change game" flow (wrapped by
 *    ui/GameSelectScreen): the SAME two-card "Select game mode" chooser as the
 *    boot gate — Classic / One-night only — plus a back pill + Escape-to-close,
 *    current mode marked. The old multi-game grid (every manifest tile) used to
 *    live here; it's gone now that Blood Moon's two modes are the whole
 *    product, not a pick among separate games.
 * Self-contained: no imports from routes/Store.tsx, so it can evolve
 * independently of the marketing page.
 */

const GOLD_RULE =
  'linear-gradient(90deg, rgba(169,118,47,0) 0%, var(--gold-3, #a9762f) 50%, rgba(169,118,47,0) 100%)';

const CARD_H = 210;
const CHAR_H = 270;
const OVERFLOW = CHAR_H - CARD_H;

export function GameSelectScreen(props: {
  /** true → player has a seat (pick a game to enter); false → game underway, they can only watch */
  seated: boolean;
  /**
   * Called with the chosen game id — the parent unlocks audio + enters. Called
   * with NO id from the paths that make no choice (spectator "Watch the game",
   * a joiner's plain "Enter the game"): the room's game is already decided and
   * inventing one here would let a later host handoff switch it (Activity.tsx).
   */
  onEnter: (gameId?: string) => void;
  /** 'switch' = the in-lobby Change-game flow: back pill, Escape closes, current game marked. */
  mode?: 'enter' | 'switch';
  /** The room's current game (marked on its card in 'switch' mode). */
  currentGameId?: string;
  /** Close without changing anything ('switch' mode only). */
  onClose?: () => void;
  /**
   * 'enter' mode only: this seat is the one that just created the room, so it
   * should be asked which game to run — the two-card "Select game mode"
   * chooser. A seat joining an ALREADY-created lobby gets a plain "Enter" tap
   * instead (the game is already chosen; see Activity.tsx's SWITCH_GAME
   * reconcile, which only ever fires for the host). Default true so any other
   * caller that doesn't pass this keeps the old always-ask behaviour.
   */
  showPicker?: boolean;
}): JSX.Element {
  const { seated, onEnter, mode = 'enter', currentGameId, onClose, showPicker = true } = props;

  // The front door is scored: the lobby bed starts here, not at the lobby.
  useMenuMusic();

  /** Every entry point into the game: voice the Ringmaster's reaction, then go. */
  function enter(gameId: string) {
    playModePickLine(gameId);
    onEnter(gameId);
  }

  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Spectator fallback (mode 'enter', !seated) still shows the single Blood
  // Moon tile from the full manifest — unrelated to the two-card chooser below.
  const bloodMoon = GAME_MANIFEST.find((g) => g.id === 'bloodmoon');

  // Both the boot-gate's first-ask AND the in-lobby/end-screen "Change game"
  // flow ('switch') land on the same fixed two-item picker.
  const isTwoCardPick = (mode === 'enter' && seated && showPicker) || mode === 'switch';
  const isSimpleEnter = mode === 'enter' && seated && !showPicker;

  return (
    <div style={outer}>
      <style>{`
        @keyframes gs-rise {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: none; }
        }
        @keyframes gs-headline {
          from { opacity: 0; transform: translateY(18px); letter-spacing: 0.14em; }
          to { opacity: 1; transform: none; letter-spacing: 0.04em; }
        }
        @keyframes gs-rule {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        .gs-anim { animation: gs-rise 0.7s var(--ease-dramatic, cubic-bezier(0.2,0.8,0.2,1)) both; }
        .gs-headline { animation: gs-headline 0.7s var(--ease-dramatic, cubic-bezier(0.2,0.8,0.2,1)) both; }
        .gs-rule { animation: gs-rule 0.9s var(--ease-dramatic, cubic-bezier(0.2,0.8,0.2,1)) both; transform-origin: center; }
        .gs-delay-0 { animation-delay: 0ms; }
        .gs-delay-1 { animation-delay: 90ms; }
        .gs-delay-2 { animation-delay: 180ms; }
        .gs-delay-3 { animation-delay: 240ms; }
        .gs-delay-4 { animation-delay: 320ms; }
        .gs-delay-5 { animation-delay: 400ms; }
        .gs-delay-6 { animation-delay: 480ms; }

        .gs-grid { display: grid; grid-template-columns: 1fr; gap: 24px; width: 100%; }
        @media (min-width: 880px) { .gs-grid { grid-template-columns: 1fr 1fr; gap: 28px; } }

        /* Character art scales down on narrow screens so it never buries the
           card's pills/wordmark (same responsive treatment as Store's .rm-char). */
        .gs-char { height: 200px; }
        @media (min-width: 560px) { .gs-char { height: ${CHAR_H}px; } }

        .gs-card { transition: transform var(--duration-base, 220ms) var(--ease-standard); }
        .gs-card:hover { transform: translateY(-4px); }
        .gs-card:hover .gs-art { box-shadow: var(--rim-gold, 0 0 24px rgba(230,184,102,0.5)); }
        .gs-card:hover .gs-char { transform: scale(1.02); }
        .gs-char { transition: transform var(--duration-base, 220ms) var(--ease-standard); }

        .gs-watch:hover { filter: brightness(0.94); transform: translateY(-1px); }
        .gs-watch:active { transform: translateY(0) scale(0.99); }

        /* ── Two-card "Select game mode" chooser (boot gate, first seat) ── */
        .gm-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 36px;
          width: 100%;
          max-width: 380px;
          margin: 0 auto;
        }
        @media (min-width: 640px) {
          .gm-grid { grid-template-columns: 1fr 1fr; max-width: 760px; gap: 32px; }
        }
        .gm-card {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          background: none;
          border: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
          font: inherit;
          color: inherit;
          text-align: center;
          -webkit-tap-highlight-color: transparent;
          transition: transform var(--duration-base, 220ms) var(--ease-standard);
        }
        .gm-card:hover { transform: translateY(-4px); }
        .gm-card:active { transform: translateY(-1px) scale(0.99); }
        .gm-frameScene {
          display: block;
          width: 100%;
        }
        .gm-frameWrap {
          position: relative;
          width: 100%;
          aspect-ratio: 1375 / 1703;
        }
        .gm-video, .gm-frame {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          pointer-events: none;
          user-select: none;
        }
        .gm-video {
          z-index: 1;
          filter: brightness(0.4) saturate(0.65);
          transition: filter 0.5s var(--ease-standard, ease);
        }
        .gm-video.is-awake { filter: brightness(1) saturate(1); }
        .gm-frame { z-index: 2; }
        /* Sits astride the frame's lower edge — the -5px overhang is deliberate
           (design), so the pill reads as mounted ON the frame, not floating
           inside it. Palette is per-mode and matches each frame's metal:
           gold for Classic, silver for One-night. */
        .gm-playersPill {
          position: absolute;
          bottom: -5px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 3;
          border-radius: 48px;
          padding: 6px 15px;
          font-family: var(--font-ui);
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          white-space: nowrap;
        }
        .gm-playersPill--gold {
          border: 2px solid #744A32;
          background: linear-gradient(86deg, #583A2F 0%, #966D42 97.18%);
        }
        .gm-playersPill--silver {
          border: 2px solid #686868;
          background: linear-gradient(86deg, #535353 0%, #4C4C4C 97.18%);
        }
        .gm-modeTitle {
          margin-top: 18px;
          font-family: var(--font-display);
          font-size: clamp(22px, 3.6vw, 30px);
          color: var(--text-primary);
        }
        .gm-modeDesc {
          margin: 8px 0 0;
          font-family: var(--font-ui);
          font-size: 13px;
          line-height: 1.5;
          color: var(--text-secondary);
          max-width: 92%;
        }
        .gm-enterBtn:hover { filter: brightness(0.94); transform: translateY(-1px); }
        .gm-enterBtn:active { transform: translateY(0) scale(0.99); }

        @media (prefers-reduced-motion: reduce) {
          .gs-anim, .gs-headline, .gs-rule { animation: none !important; opacity: 1 !important; transform: none !important; }
          .gs-card:hover, .gs-char, .gs-watch:hover, .gs-watch:active { transform: none !important; }
          .gm-card:hover, .gm-card:active, .gm-enterBtn:hover, .gm-enterBtn:active { transform: none !important; }
          .gm-video { transition: none; }
        }

        ${GLARE_CSS}
      `}</style>

      {/* Low ambient ember bed */}
      <ParticleLayer preset="embersTall" intensity={0.9} sizeScale={3} countScale={2} slot={0} />

      {/* Vignette */}
      <div aria-hidden style={vignette} />

      {/* The screen is scored from the moment it opens, so it carries the same
          volume knob as the game surface (TopBar uses the identical control). */}
      <div
        style={{
          position: 'absolute',
          top: 'max(12px, env(safe-area-inset-top))',
          right: 'clamp(12px, 2vw, 28px)',
          zIndex: 4,
        }}
      >
        <VolumeControl />
      </div>

      {/* 'switch' mode: back without changing anything. */}
      {mode === 'switch' && onClose && (
        <div style={{ position: 'absolute', top: 'max(12px, env(safe-area-inset-top))', left: 'clamp(12px, 2vw, 28px)', zIndex: 3 }}>
          <BackPill label="Back" onClick={onClose} />
        </div>
      )}

      <div style={content}>
        {/* Header */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <img
            src={bloodmoonWordmark}
            alt="Blood Moon"
            className="gs-anim gs-delay-0"
            style={{
              width: 'min(260px, 62vw)',
              display: 'block',
              filter: 'drop-shadow(0 4px 28px rgba(153,0,0,0.55))',
            }}
          />
          <h1 className="gs-headline gs-delay-1" style={headline}>
            {isTwoCardPick
              ? 'Select game mode'
              : isSimpleEnter
                ? 'The table is set.'
                : seated
                  ? 'Choose tonight’s game'
                  : 'The play is already underway.'}
          </h1>
          <div aria-hidden className="gs-rule gs-delay-2" style={rule} />
          <p className="gs-anim gs-delay-3" style={subtitle}>
            {isTwoCardPick
              ? 'A game for whatever crowd you’ve got. The Ringmaster narrates live — best with sound on.'
              : isSimpleEnter
                ? 'Take your seat — the Ringmaster narrates live, best with sound on.'
                : seated
                  ? 'The Ringmaster narrates live — best with sound on.'
                  : 'Take a seat when the table returns to the lobby — or watch tonight’s performance.'}
          </p>
        </div>

        {isTwoCardPick ? (
          <div className="gm-grid" style={{ marginTop: 'clamp(28px, 5vh, 48px)' }}>
            {MODE_CHOICES.map((choice, i) => (
              <div key={choice.gameId} className={`gs-anim gs-delay-${Math.min(4 + i, 6)}`}>
                <ModeCard
                  choice={choice}
                  onSelect={enter}
                  current={mode === 'switch' && choice.gameId === currentGameId}
                />
              </div>
            ))}
          </div>
        ) : isSimpleEnter ? (
          <div className="gs-anim gs-delay-4" style={{ marginTop: 'clamp(28px, 5vh, 48px)' }}>
            <button type="button" className="gm-enterBtn" style={watchBtn} onClick={() => onEnter()}>
              Enter the game
            </button>
          </div>
        ) : (
          <div style={{ width: '100%', marginTop: 'clamp(28px, 5vh, 48px)' }}>
            {bloodMoon && (
              <div className="gs-anim gs-delay-4" style={{ maxWidth: 460, margin: '0 auto' }}>
                <GameCard game={bloodMoon} onEnter={undefined} />
              </div>
            )}
            <div className="gs-anim gs-delay-5" style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
              <button type="button" className="gs-watch" style={watchBtn} onClick={() => onEnter()}>
                Watch the game
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Two-card "Select game mode" chooser ────────────────────────────────────

export interface ModeChoice {
  gameId: 'bloodmoon' | 'onenight';
  title: string;
  description: string;
  /** Trailing sentence of `description` to render bold — must be an exact suffix. */
  descriptionEmphasis: string;
  frameSrc: string;
  videoSrc: string;
  /** Which metal this mode's frame is, so the players pill can match it. */
  metal: 'gold' | 'silver';
  /** Subtle one-shot played when the pointer first enters this card. */
  hoverSfx: string;
}

/**
 * The two modes' title/description — exported so the lobby (LobbyContent.tsx /
 * hand/LobbyScreen.tsx) can show the SAME copy as the chooser cards, without a
 * thumbnail, once a mode is picked.
 */
export const MODE_CHOICES: ModeChoice[] = [
  // ORDER IS THE PITCH, and One Night leads (2026-07-31). Production logs: the
  // rooms that never start are the ones that cannot reach five players, while
  // One Night plays — and replays — at three. Classic still wins the night when
  // a full table shows up, so it stays a card, not a footnote; it is simply no
  // longer the first thing a group of three is asked to attempt.
  // This array is render order only — `modeChoiceById` looks up by id, and the
  // index feeds nothing but the entry-stagger delay.
  {
    gameId: 'onenight',
    title: 'One-night',
    description: 'One night, no eliminations, everyone plays to the end. One vote decides it all.',
    descriptionEmphasis: 'One vote decides it all.',
    frameSrc: frameSilver,
    videoSrc: onenightTeaser,
    metal: 'silver',
    hoverSfx: UI_SFX.hoverOneNight,
  },
  {
    gameId: 'bloodmoon',
    title: 'Classic',
    description: 'Multiple nights of suspicion, one wolf hunt to settle it. Someone dies at every dawn until the truth comes out. Built for a full table.',
    descriptionEmphasis: 'Built for a full table.',
    frameSrc: frameGold,
    videoSrc: bloodmoonTeaser,
    metal: 'gold',
    hoverSfx: UI_SFX.hoverClassic,
  },
];

/** Look up a mode's title/description by game id — `undefined` for any game
 *  outside the two-mode chooser (e.g. a legacy room still on The Whisper). */
export function modeChoiceById(gameId: string): ModeChoice | undefined {
  return MODE_CHOICES.find((c) => c.gameId === gameId);
}

/** Renders `description` with its trailing `descriptionEmphasis` sentence bold. */
export function renderModeDescription(description: string, descriptionEmphasis: string) {
  const lead = description.slice(0, description.length - descriptionEmphasis.length).trimEnd();
  return (
    <>
      {lead ? `${lead} ` : ''}
      <strong>{descriptionEmphasis}</strong>
    </>
  );
}

/**
 * ModeCard — one ornate-framed video tile in the "Select game mode" chooser.
 * The video sits BEHIND the (opaque-border, transparent-middle) frame image, so
 * sizing both to fill the same box lets the frame's own alpha mask the window —
 * no manual inset math needed.
 *
 * Video behaviour: paused + dimmed at rest, plays + undims on hover/focus.
 * `prefers-reduced-motion` never autoplays — hover still dims/undims, just no
 * playback. Touch has no hover: the card stays "awake" (never stuck dimmed)
 * and is fully selectable by tap; a single play()+pause() grabs a decoded
 * frame so the box isn't left blank (preload="metadata" alone won't render one).
 */
function ModeCard({
  choice,
  onSelect,
  current = false,
}: {
  choice: ModeChoice;
  onSelect: (gameId: string) => void;
  /** 'switch' mode only: mark the room's current mode. */
  current?: boolean;
}) {
  const reducedMotion = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [canHover, setCanHover] = useState(true);
  const [hovered, setHovered] = useState(false);
  const tilt = useTiltGlare<HTMLDivElement>();
  const hasFrameRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    setCanHover(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setCanHover(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Touch (no hover) never gets stuck in the dimmed "asleep" look.
  const awake = canHover ? hovered : true;
  const playing = canHover && hovered && !reducedMotion;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      hasFrameRef.current = true;
      v.play().catch(() => {
        // play() rejects when interrupted by a rapid hover-out — never let
        // that reject reach React as an unhandled promise/error.
      });
      return;
    }
    v.pause();
    if (!hasFrameRef.current) {
      hasFrameRef.current = true;
      v.play()
        .then(() => v.pause())
        .catch(() => {});
    }
  }, [playing]);

  const rules = GAME_RULES[choice.gameId];
  const playersLabel = rules ? `${rules.minPlayers}–${rules.maxPlayers} players` : null;

  return (
    <button
      type="button"
      className="gm-card"
      onPointerEnter={() => {
        if (!canHover) return;
        setHovered(true);
        stagePlayer.playUi(choice.hoverSfx);
      }}
      onPointerLeave={() => canHover && setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={() => onSelect(choice.gameId)}
      aria-label={`${choice.title} — ${choice.description}`}
    >
      <div className="rm-tilt-scene gm-frameScene">
        <div
          ref={tilt.ref}
          className="gm-frameWrap rm-tilt"
          onPointerMove={tilt.onPointerMove}
          onPointerLeave={tilt.onPointerLeave}
        >
          <video
            ref={videoRef}
            className={`gm-video${awake ? ' is-awake' : ''}`}
            src={choice.videoSrc}
            muted
            loop
            playsInline
            preload="metadata"
          />
          <img src={choice.frameSrc} alt="" aria-hidden className="gm-frame" draggable={false} />
          {playersLabel && (
            <span className={`gm-playersPill gm-playersPill--${choice.metal}`}>{playersLabel}</span>
          )}
          {/* Top-right (not bottom, like GameCard's) — the players pill already
              owns the bottom edge here. */}
          {current && (
            <span style={{ ...currentPill, top: 14, bottom: 'auto' }} aria-hidden>
              Current
            </span>
          )}
          <div className="rm-glare" />
        </div>
      </div>
      <div className="gm-modeTitle">{choice.title}</div>
      <p className="gm-modeDesc">
        {renderModeDescription(choice.description, choice.descriptionEmphasis)}
      </p>
    </button>
  );
}

interface GameCardEntry {
  id: string;
  name: string;
  subtitle: string;
  players: string;
  time: string;
  status: 'live' | 'coming-soon';
  character: string | null;
  cardBg: string | null;
  wordmark: string | null;
  badge?: string;
}

function GameCard({
  game,
  onEnter,
  current = false,
}: {
  game: GameCardEntry;
  onEnter: ((gameId: string) => void) | undefined;
  current?: boolean;
}) {
  const isLive = game.status === 'live' && onEnter;

  const inner = (
    <div style={{ position: 'relative', width: '100%', paddingTop: OVERFLOW }}>
      {/* Art band */}
      <div className="gs-art" style={artBand}>
        {game.cardBg && (
          <img
            src={game.cardBg}
            alt=""
            aria-hidden
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <div aria-hidden style={artOverlay} />

        {/* Pills — top-left */}
        <div style={{ position: 'absolute', top: 14, left: 14, display: 'flex', gap: 8, zIndex: 2 }}>
          {isLive ? (
            <>
              {game.players !== '—' && <InfoPill>{game.players} players</InfoPill>}
              {game.time !== '—' && <MetaPill>{game.time}</MetaPill>}
              {game.badge && <BadgePill badge={game.badge} />}
            </>
          ) : (
            <ComingSoonPill />
          )}
        </div>

        {/* 'switch' mode: mark the room's current game — bottom-right. */}
        {current && (
          <span style={currentPill} aria-hidden>
            Current
          </span>
        )}
      </div>

      {/* Character breakout */}
      {game.character && (
        <img
          src={game.character}
          alt=""
          aria-hidden
          className="gs-char"
          style={{
            position: 'absolute',
            bottom: 1,
            right: 'clamp(8px, 2.5%, 40px)',
            objectFit: 'contain',
            objectPosition: 'bottom',
            pointerEvents: 'none',
            zIndex: 3,
            filter: 'drop-shadow(0 10px 26px rgba(0,0,0,0.6))',
          }}
        />
      )}

      {/* Wordmark — bottom-left over the art */}
      <div style={{ position: 'absolute', bottom: 16, left: 18, zIndex: 2, maxWidth: '58%' }}>
        {game.wordmark ? (
          <img
            src={game.wordmark}
            alt={game.name}
            style={{
              display: 'block',
              // Omertà's wordmark is a wide lockup — cap by height (Store does the same).
              ...(game.id === 'omerta'
                ? { height: 44, width: 'auto', maxWidth: '100%' }
                : { width: '100%', maxWidth: 220 }),
            }}
          />
        ) : (
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: 'var(--text-primary)' }}>
            {game.name}
          </span>
        )}
      </div>
    </div>
  );

  if (isLive) {
    return (
      <button
        type="button"
        className="gs-card"
        style={cardBtn}
        onClick={() => onEnter(game.id)}
        aria-label={`Play ${game.name}`}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className="gs-card" style={{ ...cardBtn, cursor: 'default', opacity: 0.75 }} aria-disabled="true">
      {inner}
    </div>
  );
}

function InfoPill({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: 'var(--pill-info-bg)',
        color: 'var(--pill-info-text)',
        borderRadius: 'var(--radius-pill)',
        padding: '6px 13px',
        fontSize: 13,
        fontFamily: 'var(--font-ui)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function MetaPill({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: 'var(--pill-meta-bg)',
        color: 'var(--pill-meta-text)',
        borderRadius: 'var(--radius-pill)',
        padding: '6px 13px',
        fontSize: 13,
        fontFamily: 'var(--font-ui)',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/** Marketing badge ("Popular" gold / "BETA" arcana). */
function BadgePill({ badge }: { badge: string }) {
  const beta = badge.toLowerCase() === 'beta';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 'var(--radius-pill)',
        fontFamily: 'var(--font-ui)',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        color: beta ? 'var(--arcana-4, #c9b6ff)' : 'var(--gold-5, #e6b866)',
        background: beta ? 'rgba(44,22,80,0.85)' : 'rgba(60,41,10,0.85)',
        border: `1px solid ${beta ? 'rgba(139,92,246,0.55)' : 'rgba(230,184,102,0.5)'}`,
      }}
    >
      {badge}
    </span>
  );
}

function ComingSoonPill() {
  return (
    <span
      style={{
        background: 'rgba(255,255,255,0.14)',
        color: 'var(--text-primary)',
        border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 'var(--radius-pill)',
        padding: '6px 14px',
        fontSize: 12,
        fontFamily: 'var(--font-ui)',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      Coming soon
    </span>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const outer: CSSProperties = {
  position: 'relative',
  minHeight: '100dvh',
  width: '100%',
  background: 'linear-gradient(180deg, #080808 0%, #140002 100%), var(--bg-base)',
  overflowY: 'auto',
  overflowX: 'hidden',
  isolation: 'isolate',
  // Center the pick vertically on tall iframes; 'safe' keeps short viewports
  // scrolling from the top (unsupported values fall back to flex-start).
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'safe center',
};

const vignette: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  pointerEvents: 'none',
  background: 'radial-gradient(120% 90% at 50% 30%, transparent 50%, rgba(0,0,0,0.5) 100%)',
};

const content: CSSProperties = {
  position: 'relative',
  zIndex: 2,
  // Explicit width — as a flex item with auto cross-axis margins, `auto` width
  // would shrink-to-fit and squeeze the card grid to max-content.
  width: '100%',
  maxWidth: 1020,
  margin: '0 auto',
  padding: 'clamp(20px, 4vw, 48px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const headline: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 'clamp(28px, 4.5vw, 44px)',
  fontWeight: 400,
  letterSpacing: '0.04em',
  color: 'var(--text-primary)',
  margin: '20px 0 0',
  textShadow: '0 2px 24px rgba(0,0,0,0.85)',
};

const rule: CSSProperties = {
  width: 140,
  height: 1.5,
  background: GOLD_RULE,
  margin: '20px 0 0',
};

const subtitle: CSSProperties = {
  fontFamily: 'var(--font-ui)',
  fontSize: 15,
  lineHeight: 1.5,
  color: 'var(--text-secondary)',
  margin: '14px 0 0',
  maxWidth: 480,
};

const cardBtn: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
};

const artBand: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: CARD_H,
  maxHeight: CARD_H,
  borderRadius: 'var(--radius-card)',
  border: '1px solid var(--surface-stroke)',
  overflow: 'hidden',
  background: 'var(--surface-card)',
  boxShadow: 'var(--shadow-card)',
  transition: 'box-shadow var(--duration-base, 220ms) var(--ease-standard)',
};

const artOverlay: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.62) 100%)',
};

const currentPill: CSSProperties = {
  position: 'absolute',
  bottom: 16,
  right: 14,
  zIndex: 2,
  fontFamily: 'var(--font-ui)',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--text-primary)',
  background: 'rgba(255,255,255,0.14)',
  border: '1px solid rgba(255,255,255,0.3)',
  borderRadius: 'var(--radius-pill)',
  padding: '7px 14px',
  whiteSpace: 'nowrap',
};

const watchBtn: CSSProperties = {
  fontFamily: 'var(--font-ui)',
  fontWeight: 600,
  fontSize: 16,
  height: 52,
  padding: '0 32px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  background: 'var(--btn-primary-bg)',
  color: 'var(--btn-primary-text)',
  cursor: 'pointer',
  boxShadow: 'var(--rim-gold, 0 0 24px rgba(230,184,102,0.5))',
  transition: 'transform var(--duration-fast, 120ms) var(--ease-standard), filter var(--duration-fast, 120ms) var(--ease-standard)',
};
